/**
 * HackRF One WebUSB driver.
 *
 * Protocol constants and command helpers, ported from libhackrf's
 * `hackrf.h` / `hackrf.c`. The wire format is plain WebUSB:
 *
 *   - Vendor-specific control transfers (recipient=device, type=vendor)
 *     drive the device state machine (sample rate, frequency, gain,
 *     transceiver mode).
 *   - Bulk IN on endpoint 0x81 carries the RX I/Q stream as interleaved
 *     signed 8-bit samples (I, Q, I, Q, …). To plug into the existing
 *     RTL-SDR-shaped DSP chain we repack to offset-binary u8 in the
 *     worker before writing to the SAB ring.
 *
 * USB IDs we accept (per libhackrf):
 *   1d50:6089  HackRF One (production)
 *   1d50:604b  HackRF Jellybean (preproduction; rare, but cheap to allow)
 *   1d50:cc15  HackRF One rad1o (CCC variant)
 *
 * Endpoints and interface come from the device descriptor we read at
 * `connect()` time; the values below are the documented defaults.
 */

export const HACKRF_USB_FILTERS: USBDeviceFilter[] = [
  { vendorId: 0x1d50, productId: 0x6089 },
  { vendorId: 0x1d50, productId: 0x604b },
  { vendorId: 0x1d50, productId: 0xcc15 },
];

/** Bulk-IN endpoint for receive samples. */
export const HACKRF_RX_ENDPOINT = 1; // address 0x81 → in-endpoint number 1
/** USB interface number for the streaming endpoints. */
export const HACKRF_INTERFACE = 0;

/** Vendor-request opcodes (from `enum hackrf_vendor_request` in hackrf.h). */
export const HRF_REQ = {
  SET_TRANSCEIVER_MODE: 1,
  MAX2837_WRITE: 2,
  MAX2837_READ: 3,
  SI5351C_WRITE: 4,
  SI5351C_READ: 5,
  SAMPLE_RATE_SET: 6,
  BASEBAND_FILTER_BANDWIDTH_SET: 7,
  RFFC5071_WRITE: 8,
  RFFC5071_READ: 9,
  SPIFLASH_ERASE: 10,
  SPIFLASH_WRITE: 11,
  SPIFLASH_READ: 12,
  BOARD_ID_READ: 14,
  VERSION_STRING_READ: 15,
  SET_FREQ: 16,
  AMP_ENABLE: 17,
  BOARD_PARTID_SERIALNO_READ: 18,
  SET_LNA_GAIN: 19,
  SET_VGA_GAIN: 20,
  SET_TXVGA_GAIN: 21,
  ANTENNA_ENABLE: 23,
} as const;

/** Modes accepted by SET_TRANSCEIVER_MODE. We only use OFF and RX. */
export const HRF_MODE = {
  OFF: 0,
  RX: 1,
  TX: 2,
  SWEEP: 5,
} as const;

/** Pack a frequency (Hz) into the 8-byte payload SET_FREQ wants:
 *  4 bytes little-endian MHz integer, 4 bytes little-endian remainder Hz.
 *  We return ArrayBuffer (not Uint8Array) so WebUSB's `BufferSource`
 *  parameter type matches without a cast on strict TS settings. */
export function packSetFreqPayload(freqHz: number): ArrayBuffer {
  const mhz = Math.floor(freqHz / 1_000_000);
  const remainder = Math.round(freqHz - mhz * 1_000_000);
  const buf = new ArrayBuffer(8);
  const v = new DataView(buf);
  v.setUint32(0, mhz, true);
  v.setUint32(4, remainder, true);
  return buf;
}

/** Pack a sample rate into the 8-byte payload SAMPLE_RATE_SET expects:
 *  4 bytes LE numerator (freq Hz), 4 bytes LE divider. Setting divider=1
 *  gives us exact rate; the device's PLL handles non-integer ratios. */
export function packSampleRatePayload(rateHz: number): ArrayBuffer {
  const buf = new ArrayBuffer(8);
  const v = new DataView(buf);
  v.setUint32(0, Math.round(rateHz), true);
  v.setUint32(4, 1, true);
  return buf;
}

/** Board IDs returned by `BOARD_ID_READ` (per libhackrf hackrf.h). */
export const HACKRF_BOARD_NAMES: Record<number, string> = {
  0: 'Jellybean',
  1: 'Jawbreaker',
  2: 'HackRF One',
  3: 'Rad1o',
};

export type HackRfBoardInfo = {
  /** Raw board ID. */
  boardId: number;
  /** Human-readable board name (HackRF One, Rad1o, …). */
  boardName: string;
  /** Firmware version string read from the device. */
  firmwareVersion: string;
  /** 64-bit part ID as a hex string (`xxxxxxxxxxxxxxxx`). */
  partId: string;
  /** 128-bit serial number as a hex string. Often used as the device's
   *  unique fingerprint across reboots. */
  serialNumber: string;
};

/** Vendor-IN control transfer that reads `length` bytes from the device. */
export async function vendorIn(
  device: USBDevice,
  request: number,
  value: number,
  index: number,
  length: number,
): Promise<DataView> {
  const r = await device.controlTransferIn(
    {
      requestType: 'vendor',
      recipient: 'device',
      request,
      value,
      index,
    },
    length,
  );
  if (r.status !== 'ok' || !r.data) {
    throw new Error(`HackRF control_in request ${request} failed: ${r.status}`);
  }
  return r.data;
}

/** Read BOARD_ID + VERSION_STRING + PARTID_SERIALNO and assemble a full
 *  device-info record. Safe to call after `claimInterface`; doesn't require
 *  the transceiver to be in any specific mode. */
export async function readHackRfBoardInfo(device: USBDevice): Promise<HackRfBoardInfo> {
  // BOARD_ID_READ → 1 byte.
  const idView = await vendorIn(device, HRF_REQ.BOARD_ID_READ, 0, 0, 1);
  const boardId = idView.getUint8(0);
  const boardName = HACKRF_BOARD_NAMES[boardId] ?? `unknown (${boardId})`;

  // VERSION_STRING_READ → up to 255 bytes of ASCII.
  const verView = await vendorIn(device, HRF_REQ.VERSION_STRING_READ, 0, 0, 255);
  const verBytes = new Uint8Array(verView.buffer, verView.byteOffset, verView.byteLength);
  let firmwareVersion = new TextDecoder('utf-8', { fatal: false }).decode(verBytes);
  // Trim trailing NULs and whitespace.
  // eslint-disable-next-line no-control-regex
  firmwareVersion = firmwareVersion.replace(/ +$/g, '').trim();

  // BOARD_PARTID_SERIALNO_READ → 24 bytes (8 part ID + 16 serial).
  const sn = await vendorIn(device, HRF_REQ.BOARD_PARTID_SERIALNO_READ, 0, 0, 24);
  const partId = bytesToHex(sn, 0, 8);
  const serialNumber = bytesToHex(sn, 8, 16);

  return { boardId, boardName, firmwareVersion, partId, serialNumber };
}

function bytesToHex(view: DataView, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length; i++) {
    const b = view.getUint8(offset + i);
    s += b.toString(16).padStart(2, '0');
  }
  return s;
}

/** Send a vendor-out control transfer with no data payload. */
export async function vendorOutNoData(
  device: USBDevice,
  request: number,
  value: number,
  index: number,
): Promise<void> {
  const r = await device.controlTransferOut({
    requestType: 'vendor',
    recipient: 'device',
    request,
    value,
    index,
  });
  if (r.status !== 'ok') {
    throw new Error(`HackRF control_out request ${request} failed: ${r.status}`);
  }
}

/** Send a vendor-out control transfer with a small data payload. */
export async function vendorOutWithData(
  device: USBDevice,
  request: number,
  value: number,
  index: number,
  data: BufferSource,
): Promise<void> {
  const r = await device.controlTransferOut(
    {
      requestType: 'vendor',
      recipient: 'device',
      request,
      value,
      index,
    },
    data,
  );
  if (r.status !== 'ok') {
    throw new Error(`HackRF control_out (with data) request ${request} failed: ${r.status}`);
  }
}

/**
 * Several "set" requests on the HackRF (gain stages, antenna power) are
 * actually defined as IN transfers in firmware — the device returns a
 * 1-byte status indicating whether it accepted the value. Using
 * controlTransferOut for these triggers a STALL with "transfer error".
 */
export async function vendorIn1Byte(
  device: USBDevice,
  request: number,
  value: number,
  index: number,
): Promise<number> {
  const r = await device.controlTransferIn(
    {
      requestType: 'vendor',
      recipient: 'device',
      request,
      value,
      index,
    },
    1,
  );
  if (r.status !== 'ok' || !r.data || r.data.byteLength < 1) {
    throw new Error(`HackRF control_in request ${request} failed: ${r.status}`);
  }
  return r.data.getUint8(0);
}

// ─── High-level helpers ──────────────────────────────────────────────────

/** Clamp LNA gain to the device's 0..40 dB grid (8 dB steps). */
export function clampLnaDb(db: number): number {
  const stepped = Math.round(db / 8) * 8;
  return Math.max(0, Math.min(40, stepped));
}

/** Clamp VGA (baseband) gain to the 0..62 dB grid (2 dB steps). */
export function clampVgaDb(db: number): number {
  const stepped = Math.round(db / 2) * 2;
  return Math.max(0, Math.min(62, stepped));
}

/** RF amplifier gain when on (per HackRF docs: ~11 dB pre-amp). */
export const HACKRF_AMP_DB = 11;

/**
 * HackRF's three independent gain stages. Per the official docs:
 *   RF/AMP   — on/off, +11 dB when on
 *   IF/LNA   — 0..40 dB in 8 dB steps (post-mixer)
 *   BB/VGA   — 0..62 dB in 2 dB steps (baseband)
 * Recommended starting point: RF=off, IF=16, BB=16. Adjust IF and BB
 * roughly equally; only enable RF when signals are weak.
 */
export type HackRfGain = { ampOn: boolean; lnaDb: number; vgaDb: number };

export const HACKRF_DEFAULT_GAIN: HackRfGain = {
  ampOn: false,
  lnaDb: 16,
  vgaDb: 16,
};

/**
 * Fallback gain distribution from a single user-facing dB value, used
 * only when the per-stage controls haven't been initialised yet (e.g.
 * AGC selected on the unified gain dial). Greedy split: AMP at high
 * total gain, LNA before VGA (LNA has the lower noise figure).
 */
export function distributeGain(userDb: number): HackRfGain {
  const ampOn = userDb >= 50;
  const remaining = userDb - (ampOn ? HACKRF_AMP_DB : 0);
  const lnaDb = clampLnaDb(Math.min(40, Math.max(0, remaining)));
  const afterLna = Math.max(0, remaining - lnaDb);
  const vgaDb = clampVgaDb(Math.min(62, afterLna));
  return { ampOn, lnaDb, vgaDb };
}
